import React, { useState, useEffect } from 'react';
import { useParams, Navigate, Link, useLocation } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/layout/Navbar';
import PageHeader from '../components/layout/PageHeader';
import './SponsorPages.css';
import { Pencil, Settings, Image as ImageIcon } from 'lucide-react';

/**
 * Sponsor settings — where a sponsor records their own sponsorship agreement
 * (package cost + term). Deliberately separate from the attribution report so
 * the cost they pay isn't put front-and-centre on their performance dashboard;
 * it just quietly powers the return-on-spend maths there.
 */
const SponsorSettingsPage = () => {
  const { clubSlug } = useParams();
  const location = useLocation(); // UC-B5: return path for /login
  const { user, loading: authLoading, getMembership } = useAuth();
  const membership = getMembership(clubSlug);
  const qc = useQueryClient();

  const { data: club } = useQuery({
    queryKey: ['club', clubSlug],
    queryFn: () => axios.get(`/clubs/${clubSlug}`).then(r => r.data),
  });

  // The logged-in sponsor's own record (business name + logo) for the header.
  const { data: me } = useQuery({
    queryKey: ['sponsor-me', clubSlug],
    queryFn: () => axios.get(`/clubs/${clubSlug}/sponsors/me`).then(r => r.data),
    enabled: !!membership,
  });

  const { data, isLoading } = useQuery({
    queryKey: ['sponsor-attribution', clubSlug, '30d'],
    queryFn: () => axios.get(`/clubs/${clubSlug}/sponsors/report/redemptions?period=30d`).then(r => r.data),
    enabled: !!user && !!membership,
  });

  const mine = data?.breakdown?.[0];
  const [form, setForm] = useState({ sponsorshipCost: '', sponsorshipStart: '', sponsorshipEnd: '' });
  const [saved, setSaved] = useState(false);
  const [logoPreview, setLogoPreview] = useState(null);

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => setLogoPreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const saveLogo = useMutation({
    mutationFn: () => axios.patch(`/clubs/${clubSlug}/sponsors/me`, { logo_url: logoPreview }).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sponsor-me', clubSlug] });
      setLogoPreview(null);
    },
    onError: (err) => alert(err.response?.data?.error || 'Failed to save logo'),
  });

  // Hydrate the form once the sponsor's record loads.
  useEffect(() => {
    if (mine) {
      setForm({
        sponsorshipCost: mine.sponsorship_cost != null ? String(mine.sponsorship_cost) : '',
        sponsorshipStart: mine.sponsorship_start ? String(mine.sponsorship_start).slice(0, 10) : '',
        sponsorshipEnd: mine.sponsorship_end ? String(mine.sponsorship_end).slice(0, 10) : '',
      });
    }
  }, [mine?.sponsor_id, mine?.sponsorship_cost, mine?.sponsorship_start, mine?.sponsorship_end]);

  const save = useMutation({
    mutationFn: () => axios.patch(`/clubs/${clubSlug}/sponsors/${mine.sponsor_id}/agreement`, form).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sponsor-attribution'] });
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    },
  });

  if (authLoading) return <div className="page-loading"><div className="spinner" style={{ width: 40, height: 40 }} /></div>;
  if (!user) return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  if (membership && membership.role !== 'sponsor' && membership.role !== 'admin') {
    return <Navigate to={`/${clubSlug}`} replace />;
  }

  const isAdmin = membership?.role === 'admin';

  return (
    <div className="sponsor-page">
      <Navbar club={club} />

      <PageHeader
        icon={Settings}
        logoUrl={me?.logo_url || undefined}
        title="Sponsorship Settings"
        subtitle={<>{me?.business_name || `${user.firstName} ${user.lastName}`} · {club?.name || clubSlug}</>}
      />

      <div className="sponsor-content scan-content">
        {me && (
          <div className="sponsor-section">
            <h2 className="sponsor-section-heading"><ImageIcon size={16} /> Your logo</h2>
            <div className="card" style={{ padding: '18px 20px' }}>
              <p style={{ fontSize: 13.5, color: 'var(--text-secondary)', marginTop: 0 }}>
                Shown next to your offers in the members' app and on your dashboard. A square image works best.
              </p>
              <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
                <div style={{ width: 64, height: 64, borderRadius: 12, overflow: 'hidden', background: 'rgba(196,148,58,0.10)', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid var(--border)', flexShrink: 0 }}>
                  {(logoPreview || me.logo_url)
                    ? <img src={logoPreview || me.logo_url} alt="Your logo" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    : <span style={{ color: '#8A6220', fontWeight: 700, fontSize: 22 }}>{(me.business_name || '?').charAt(0)}</span>}
                </div>
                <label className="btn btn-secondary btn-sm" style={{ cursor: 'pointer', margin: 0 }}>
                  {(logoPreview || me.logo_url) ? 'Replace' : 'Upload logo'}
                  <input type="file" accept="image/*" style={{ display: 'none' }} onChange={handleLogoChange} />
                </label>
                {logoPreview && (
                  <button className="btn btn-primary btn-sm" onClick={() => saveLogo.mutate()} disabled={saveLogo.isPending}>
                    {saveLogo.isPending ? 'Saving…' : 'Save logo'}
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
        <div className="sponsor-section">
          <h2 className="sponsor-section-heading"><Pencil size={16} /> Your sponsorship agreement</h2>
          <div className="card" style={{ padding: '18px 20px' }}>
            <p style={{ fontSize: 13.5, color: 'var(--text-secondary)', marginTop: 0 }}>
              Recorded here so your <Link to={`/${clubSlug}/attribution`} style={{ color: 'var(--green)' }}>attribution dashboard</Link> can
              show your return on spend. Only you and your club admin can see this.
            </p>

            {isAdmin ? (
              <p style={{ color: 'var(--text-muted)', fontSize: 14 }}>
                You're viewing as a club admin — manage every sponsor's package cost &amp; dates under <strong>Admin → Sponsors</strong>.
              </p>
            ) : isLoading ? (
              <p style={{ color: 'var(--text-muted)' }}>Loading…</p>
            ) : !mine ? (
              <p style={{ color: 'var(--text-muted)' }}>
                No sponsor record is linked to your account yet — your club admin can set this up.
              </p>
            ) : (
              <div style={{ display: 'grid', gap: 14 }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12 }}>
                  <div className="form-group">
                    <label className="label">Package cost (£)</label>
                    <input className="input" type="number" min="0" step="0.01" placeholder="e.g. 500"
                      value={form.sponsorshipCost}
                      onChange={e => setForm(f => ({ ...f, sponsorshipCost: e.target.value }))} />
                  </div>
                  <div className="form-group">
                    <label className="label">Start date</label>
                    <input className="input" type="date" value={form.sponsorshipStart}
                      onChange={e => setForm(f => ({ ...f, sponsorshipStart: e.target.value }))} />
                  </div>
                  <div className="form-group">
                    <label className="label">End date</label>
                    <input className="input" type="date" value={form.sponsorshipEnd}
                      onChange={e => setForm(f => ({ ...f, sponsorshipEnd: e.target.value }))} />
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                  <button className="btn btn-primary" style={{ padding: '9px 20px' }}
                    disabled={save.isPending} onClick={() => save.mutate()}>
                    {save.isPending ? 'Saving…' : 'Save agreement'}
                  </button>
                  {saved && <span style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--success)' }}>Saved ✓</span>}
                  {save.isError && <span style={{ fontSize: 13, color: 'var(--danger)' }}>Could not save — try again.</span>}
                </div>
                <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: 0 }}>
                  The cost is spread across these dates and matched to the dashboard period, so cost-per-redemption stays accurate.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SponsorSettingsPage;
